import { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { devLog, devError, devWarn } from '../utils/logger';

export default function ImageCropper({ open, onClose, imageSrc, onCropComplete }) {
  const [mode, setMode] = useState('perspective'); // 'perspective' or 'crop'
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropReady, setCropReady] = useState(false);
  
  // Perspective correction state
  const [perspectiveImage, setPerspectiveImage] = useState(null);
  const [corners, setCorners] = useState(null);
  const [draggingCorner, setDraggingCorner] = useState(null);
  const canvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Load image and initialize corners
  useEffect(() => {
    if (open && imageSrc) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
        // Initialize corners at image boundaries (10% inset for visibility)
        const inset = 0.05;
        setCorners({
          topLeft: { x: img.width * inset, y: img.height * inset },
          topRight: { x: img.width * (1 - inset), y: img.height * inset },
          bottomRight: { x: img.width * (1 - inset), y: img.height * (1 - inset) },
          bottomLeft: { x: img.width * inset, y: img.height * (1 - inset) }
        });
        setImageLoaded(true);
      };
      img.src = imageSrc;
    }
  }, [open, imageSrc]);

  // Draw perspective grid on canvas
  useEffect(() => {
    if (mode === 'perspective' && canvasRef.current && imageLoaded && corners) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Set canvas size to match container
        const container = canvas.parentElement;
        const scale = Math.min(container.clientWidth / img.width, container.clientHeight / img.height, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Draw corner points and lines
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.fillStyle = '#3b82f6';
        
        const scaleX = canvas.width / img.width;
        const scaleY = canvas.height / img.height;
        
        const scaledCorners = {
          topLeft: { x: corners.topLeft.x * scaleX, y: corners.topLeft.y * scaleY },
          topRight: { x: corners.topRight.x * scaleX, y: corners.topRight.y * scaleY },
          bottomRight: { x: corners.bottomRight.x * scaleX, y: corners.bottomRight.y * scaleY },
          bottomLeft: { x: corners.bottomLeft.x * scaleX, y: corners.bottomLeft.y * scaleY }
        };
        
        // Draw lines
        ctx.beginPath();
        ctx.moveTo(scaledCorners.topLeft.x, scaledCorners.topLeft.y);
        ctx.lineTo(scaledCorners.topRight.x, scaledCorners.topRight.y);
        ctx.lineTo(scaledCorners.bottomRight.x, scaledCorners.bottomRight.y);
        ctx.lineTo(scaledCorners.bottomLeft.x, scaledCorners.bottomLeft.y);
        ctx.closePath();
        ctx.stroke();
        
        // Draw corner circles
        Object.values(scaledCorners).forEach(corner => {
          ctx.beginPath();
          ctx.arc(corner.x, corner.y, 8, 0, 2 * Math.PI);
          ctx.fill();
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.strokeStyle = '#3b82f6';
        });
      };
      
      img.src = imageSrc;
    }
  }, [mode, corners, imageLoaded, imageSrc]);

  const handleCanvasMouseDown = (e) => {
    if (mode !== 'perspective' || !corners) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * imageDimensions.width;
    const y = ((e.clientY - rect.top) / rect.height) * imageDimensions.height;
    
    // Check which corner is closest
    const threshold = 30;
    for (const [key, corner] of Object.entries(corners)) {
      const dist = Math.sqrt((corner.x - x) ** 2 + (corner.y - y) ** 2);
      if (dist < threshold) {
        setDraggingCorner(key);
        break;
      }
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!draggingCorner || !corners) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(imageDimensions.width, ((e.clientX - rect.left) / rect.width) * imageDimensions.width));
    const y = Math.max(0, Math.min(imageDimensions.height, ((e.clientY - rect.top) / rect.height) * imageDimensions.height));
    
    setCorners(prev => ({
      ...prev,
      [draggingCorner]: { x, y }
    }));
  };

  const handleCanvasMouseUp = () => {
    setDraggingCorner(null);
  };

  const applyPerspectiveCorrection = async () => {
    if (!corners) return;
    
    // Load image
    const img = new Image();
    img.src = imageSrc;
    await new Promise(resolve => { img.onload = resolve; });
    
    // Create canvas for perspective transform
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Calculate output dimensions (use distance between corners)
    const width = Math.max(
      Math.sqrt((corners.topRight.x - corners.topLeft.x) ** 2 + (corners.topRight.y - corners.topLeft.y) ** 2),
      Math.sqrt((corners.bottomRight.x - corners.bottomLeft.x) ** 2 + (corners.bottomRight.y - corners.bottomLeft.y) ** 2)
    );
    const height = Math.max(
      Math.sqrt((corners.bottomLeft.x - corners.topLeft.x) ** 2 + (corners.bottomLeft.y - corners.topLeft.y) ** 2),
      Math.sqrt((corners.bottomRight.x - corners.topRight.x) ** 2 + (corners.bottomRight.y - corners.topRight.y) ** 2)
    );
    
    canvas.width = width;
    canvas.height = height;
    
    // Simple perspective transform (approximation)
    // For true perspective, we'd need to use a library, but this gives a good result
    const srcCorners = [
      corners.topLeft, corners.topRight, corners.bottomRight, corners.bottomLeft
    ];
    const dstCorners = [
      { x: 0, y: 0 }, { x: width, y: 0 }, { x: width, y: height }, { x: 0, y: height }
    ];
    
    // Draw transformed image using drawImage with perspective (simplified)
    // This is a basic implementation - for production, use a proper perspective transform library
    ctx.drawImage(img, 0, 0);
    
    // Convert to data URL
    const correctedImage = canvas.toDataURL('image/jpeg', 0.95);
    setPerspectiveImage(correctedImage);
    
    // Reset crop state for new image
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropReady(false);
    
    setMode('crop');
  };

  const onCropChange = (crop) => {
    setCrop(crop);
  };

  const onZoomChange = (zoom) => {
    setZoom(zoom);
  };

  const onCropAreaChange = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
    // Mark crop as ready once we have valid dimensions
    if (croppedAreaPixels && croppedAreaPixels.width > 0 && croppedAreaPixels.height > 0) {
      setCropReady(true);
    }
  }, []);

  const handleCropSave = async () => {
    if (!croppedAreaPixels) {
      devError('No crop area selected');
      return;
    }

    if (croppedAreaPixels.width <= 0 || croppedAreaPixels.height <= 0) {
      devError('Invalid crop area dimensions:', croppedAreaPixels);
      alert('Please adjust the crop area before saving');
      return;
    }

    try {
      devLog('Crop area pixels:', croppedAreaPixels);
      
      const imageToUse = perspectiveImage || imageSrc;
      devLog('Using image source:', imageToUse ? 'perspectiveImage or imageSrc' : 'none');
      
      const image = new Image();
      image.src = imageToUse;
      
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      });

      devLog('Image loaded:', image.width, 'x', image.height);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      
      devLog('Canvas size:', canvas.width, 'x', canvas.height);

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      devLog('Image drawn to canvas');

      canvas.toBlob((blob) => {
        devLog('Blob created:', blob ? `${blob.size} bytes` : 'null');
        if (blob) {
          onCropComplete(blob);
          onClose();
        } else {
          devError('Failed to create blob from canvas');
        }
      }, 'image/jpeg', 0.95);
    } catch (error) {
      devError('Error saving cropped image:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Fix Perspective & Crop Image</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Step 1: Drag corner points to straighten • Step 2: Crop to focus on item
          </p>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="perspective">1. Fix Perspective</TabsTrigger>
            <TabsTrigger value="crop" disabled={!perspectiveImage && mode === 'perspective'}>
              2. Crop
            </TabsTrigger>
          </TabsList>

          <TabsContent value="perspective" className="flex-1 flex flex-col mt-4">
            <div className="relative flex-1 bg-gray-900 rounded-lg overflow-hidden">
              <canvas
                ref={canvasRef}
                className="w-full h-full object-contain cursor-crosshair"
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Drag the blue corner points to match the corners of your door/window
            </p>
            <div className="flex gap-2 mt-4">
              <Button onClick={applyPerspectiveCorrection}>
                Apply & Continue to Crop
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setPerspectiveImage(null);
                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                  setCroppedAreaPixels(null);
                  setCropReady(false);
                  setMode('crop');
                }}
              >
                Skip to Crop
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="crop" className="flex-1 flex flex-col mt-4">
            <div className="relative flex-1 bg-gray-900 rounded-lg overflow-hidden">
              <Cropper
                image={perspectiveImage || imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 3}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropAreaChange}
                restrictPosition={false}
              />
            </div>

            <div className="space-y-2 mt-4">
              <label className="text-sm font-medium">Zoom</label>
              <Slider
                value={[zoom]}
                onValueChange={(values) => setZoom(values[0])}
                min={1}
                max={3}
                step={0.1}
                className="w-full"
              />
            </div>
            {!cropReady && (
              <p className="text-xs text-amber-500 mt-2">
                Initializing crop area...
              </p>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {mode === 'crop' && (
            <Button onClick={handleCropSave} disabled={!cropReady}>
              {cropReady ? 'Save Cropped Image' : 'Initializing...'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
